/**
 * 名前空間の登録簿 (admin-english-toggle-20260924 phase 2 / ADR-081)。
 *
 * 名前空間は `i18n/namespaces/{ns}.ts` が自分で登録する (`defineNamespace`)。画面はその
 * handle を import して `useT(handle)` に渡すので、辞書は **handle を import した画面の chunk**
 * に入る (lazy な画面の辞書は main chunk に入らない)。共有の集約ファイルが無いので、
 * 名前空間を足す作業 (phase 2 の slice) 同士がここで衝突しない。
 *
 * - 同じ名前を 2 回登録したら、テスト (と HMR の無い開発実行) では例外にする (別の名前空間を同じ名前で
 *   上書きしない。名前の重複は `dictionaries.test.ts` のファイル名 = 名前空間名の検査でも見つかる)。
 *   開発サーバー (HMR あり) では、同じファイルの読み直しで登録し直すので置き換える。本番では最初の登録を残す
 *   (画面を落とさない)。
 * - `knownMessages`: 作る側が日本語で作り、表示する直前 (ToastContainer) にだけ訳す共通メッセージの
 *   キー (`knownMessages.ts`)。名前空間ごとに登録できるので、共有の一覧を編集しなくてよい。
 */
import type { DictionaryTree, LeafKey, Locale, NamespaceHandle, TranslationOf } from './types';

interface RegisteredNamespace {
  readonly name: string;
  readonly dictionaries: Readonly<Record<Locale, DictionaryTree>>;
  readonly knownMessageKeys: readonly string[];
}

export interface DefineNamespaceOptions<Ja> {
  /** 表示の直前でだけ訳す共通メッセージのキー (日本語の文言と完全一致したものを訳す)。 */
  readonly knownMessages?: readonly LeafKey<Ja>[];
}

const REGISTRY = new Map<string, RegisteredNamespace>();

/** 開発 / テストの実行か (本番 build では false)。 */
function isDevOrTest(): boolean {
  return Boolean(import.meta.env?.DEV) || import.meta.env?.MODE === 'test';
}

/** HMR で読み直されている最中か (開発サーバーだけ)。 */
function isHotReload(): boolean {
  return Boolean(import.meta.hot);
}

/**
 * 名前空間を登録し、画面が `useT` / `createT` に渡す handle を返す。
 * 英語は `TranslationOf<Ja>` で型付けされるので、キーの欠落・余分は `tsc -b` のエラーになる。
 */
export function defineNamespace<Ja extends DictionaryTree>(
  name: string,
  ja: Ja,
  en: NoInfer<TranslationOf<Ja>>,
  options?: NoInfer<DefineNamespaceOptions<Ja>>,
): NamespaceHandle<Ja> {
  const entry: RegisteredNamespace = {
    name,
    // TranslationOf<Ja> は Ja と同じ形の文字列の木 (DictionaryTree として探索できる)
    dictionaries: { ja, en: en as DictionaryTree },
    knownMessageKeys: [...(options?.knownMessages ?? [])],
  };
  if (REGISTRY.has(name) && !isHotReload()) {
    if (isDevOrTest()) {
      throw new Error(`i18n: 名前空間 "${name}" は既に登録されています (名前の重複)`);
    }
    return { name, ja };
  }
  REGISTRY.set(name, entry);
  return { name, ja };
}

/** 登録済みの名前空間の辞書 (無ければ undefined)。 */
export function namespaceDictionary(name: string, locale: Locale): DictionaryTree | undefined {
  return REGISTRY.get(name)?.dictionaries[locale];
}

/** 登録済みの名前空間の名前 (登録順)。テストと診断用。 */
export function registeredNamespaceNames(): string[] {
  return [...REGISTRY.keys()];
}

/** 表示の直前で訳す共通メッセージ (登録順)。 */
export function registeredKnownMessages(): { namespace: string; key: string }[] {
  return [...REGISTRY.values()].flatMap((entry) =>
    entry.knownMessageKeys.map((key) => ({ namespace: entry.name, key })),
  );
}
